import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Clock } from "lucide-react";

const CORINNE = "corinne.golfier@fautras.com";

// Signed client order: deposit received + financing choice; Corinne gets the order once both are set.
export const OrderTracking = ({ quote, editable, onChange, canFinance = true }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const n = quote.quote_number;
  const sent = !!quote.corinne_sent || (quote.send_log || []).some((l) => (l.to || []).includes(CORINNE));
  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/quotes/${quote.id}/order-tracking`, patch);
      onChange(data);
      if (data.corinne === "sent" && !sent) toast.success(t("Commande transmise à Corinne Golfier"));
      if (data.corinne === "failed") toast.error(t("Envoi à Corinne impossible pour le moment : il sera retenté à la prochaine modification."));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-1.5 flex flex-col gap-1 text-xs min-w-[190px]" data-testid={`order-tracking-${n}`}>
      {quote.out_of_sector
        ? <span className="text-[10px] font-semibold text-amber-700" data-testid={`out-of-sector-${n}`}>{t("Commande hors secteur : validation manager")}</span>
        : <span className="text-[10px] font-semibold text-emerald-700" data-testid={`in-sector-${n}`}>{t("Commande secteur")}</span>}
      {quote.has_financing && !canFinance && <span className="text-[10px] font-semibold text-amber-700" data-testid={`financing-mgr-${n}`}>{t("Commande avec financement : gérée par le manager")}</span>}
      <label className="flex items-center gap-1.5 cursor-pointer">
        <Checkbox checked={!!quote.deposit_received} disabled={!editable || busy} onCheckedChange={(v) => save({ deposit_received: !!v })} data-testid={`deposit-${n}`} />
        {t("Acompte reçu")}
      </label>
      <Select value={quote.financing_choice || "unset"} disabled={!editable || busy} onValueChange={(v) => save({ financing_choice: v === "unset" ? "" : v })}>
        <SelectTrigger className="h-7 w-auto text-xs" data-testid={`financing-choice-${n}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="unset">{t("Financement : à choisir")}</SelectItem>
          <SelectItem value="ok" disabled={!canFinance} data-testid={`financing-ok-${n}`}>{t("Financement OK")}</SelectItem>
          <SelectItem value="study" disabled={!canFinance} data-testid={`financing-study-${n}`}>{t("Financement en étude")}</SelectItem>
          <SelectItem value="none" data-testid={`financing-none-${n}`}>{t("Pas de financement")}</SelectItem>
        </SelectContent>
      </Select>
      <span className={`flex items-center gap-1 ${sent ? "text-emerald-700" : "text-muted-foreground"}`} data-testid={`corinne-status-${n}`}>
        {sent ? <Send className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
        {sent ? t("Transmise à Corinne") : quote.out_of_sector ? t("Hors secteur : envoyée seulement à contact@fautras.com") : quote.stock_order === "stock" ? t("Véhicule de stock : envoyée seulement à contact@fautras.com") : t("Pas encore transmise à Corinne")}
      </span>
    </div>
  );
};
